/**
 * Founding Donors — data layer.
 *
 * Talks to the /api/founding-donors backend. Exposes:
 *   - fetchFoundingDonors() → { donors: [], count: 0 }
 *   - submitFoundingPledge(body) → { pending, checkout_url, message }
 *
 * The server merges hard-coded seed donors with real paid pledges and
 * returns them alphabetized by last name — this file is a thin wrapper.
 */
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Local fallback used only if the API is unreachable — same 6 donors the
// backend seeds. Keeps the wall from going empty during a network blip.
export const SEED_FALLBACK = [
  { full_name: "Penny Codrington", joined_date: "2026-01-15" },
  { full_name: "Kristi Holden",     joined_date: "2026-01-18" },
  { full_name: "Katrina Jackson",  joined_date: "2026-01-22" },
  { full_name: "Angela James",      joined_date: "2026-01-24" },
  { full_name: "Blonzine James",    joined_date: "2026-01-27" },
  { full_name: "Willie Sanders",    joined_date: "2026-02-02" },
];

export const fetchFoundingDonors = async () => {
  try {
    const { data } = await axios.get(`${API}/founding-donors`);
    return data;
  } catch (err) {
    return { donors: SEED_FALLBACK, count: SEED_FALLBACK.length };
  }
};

export const submitFoundingPledge = async (body) => {
  const bodyWithOrigin = {
    ...body,
    origin_url: typeof window !== "undefined" ? window.location.origin : "",
  };
  const { data } = await axios.post(`${API}/founding-donors/pledge`, bodyWithOrigin);
  return data;
};

/**
 * Group an array of donor objects into { letter: [donor, ...] } buckets,
 * keyed by the first letter of the LAST word of full_name.
 * "Anonymous" and single-word names bucket by the first letter.
 */
export const groupByLastNameLetter = (donors) => {
  const buckets = {};
  for (const d of donors) {
    const name = (d.full_name || "").trim();
    if (!name) continue;
    const parts = name.split(/\s+/);
    const surname = parts.length > 1 ? parts[parts.length - 1] : name;
    const letter = surname[0]?.toUpperCase() || "?";
    if (!buckets[letter]) buckets[letter] = [];
    buckets[letter].push(d);
  }
  // Return in alphabetical order
  return Object.keys(buckets)
    .sort()
    .map((letter) => ({ letter, donors: buckets[letter] }));
};
